import config from '../../../../common/utils/constants/config';
import { mapStyleTypes } from '../../../utils/constants/map-styles';
import {
  removeLayersWhenMapReady,
  createGetLayerIdWithPostfix,
  getLayersIdUniquePostfix,
} from '../../../utils/helpers/interactiveLayerStyleUtils';
import { Expression } from 'mapbox-gl';
import { MIN_MAP_POI_ZOOM_LEVEL } from '../../../../common/utils/constants/constants.ts';

export const tileset = config.POI_TILESET;
export const sourceId = config.POI_SOURCE_ID;
export const sourceLayer = config.POI_SOURCE_LAYER;

const getDefaultLayerId = createGetLayerIdWithPostfix('default_poi');
const getHoveredLayerId = createGetLayerIdWithPostfix('hovered_poi');
const getSelectedLayerId = createGetLayerIdWithPostfix('selected_poi');
const getCircleLayerId = createGetLayerIdWithPostfix('circle_poi');
const getCircleLayerHoveredId =
  createGetLayerIdWithPostfix('circle_hovered_poi');

const textColorVector = [
  'match',
  ['get', 'maki'],
  [
    'beer',
    'restaurant',
    'cafe',
    'restaurant-seafood',
    'teahouse',
    'bbq',
    'bar',
    'restaurant-bbq',
    'restaurant-sushi',
    'restaurant-pizza',
    'restaurant-noodle',
    'ice-cream',
    'fast-food',
    'karaoke',
  ],
  '#ed6b07',
  ['lodging'],
  '#d87bf6',
  [
    'shoe',
    'clothing_store',
    'fitness-centre',
    'jewelry-store',
    'shop',
    'bakery',
    'fuel',
    'alcohol-shop',
    'car-rental',
    'florist',
    'garden-centre',
    'watch',
    'furniture',
    'optician',
    'gift',
    'convenience',
    'confectionery',
    'laundry',
    'grocery',
    'commercial',
    'hairdresser',
  ],
  '#007afc',
  [
    'volcano',
    'natural',
    'dog-park',
    'racetrack',
    'cemetery-JP',
    'park',
    'hot-spring',
    'campsite',
    'cemetery',
    'highway-rest-area',
    'park-alt1',
    'playground',
    'mountain',
    'viewpoint',
    'waterfall',
    'wetland',
    'beach',
    'garden',
  ],
  '#749d3f',
  ['dentist', 'doctor', 'hospital', 'veterinary', 'pharmacy', 'hospital-JP'],
  '#ff8368',
  [
    'landmark-JP',
    'stadium',
    'monument-JP',
    'amusement-park',
    'library',
    'castle-JP',
    'cinema',
    'castle',
    'theatre',
    'casino',
    'art-gallery',
    'landmark',
    'bowling-alley',
    'monument',
    'museum',
    'aquarium',
    'zoo',
    'attraction',
  ],
  'hsl(164, 68%, 39%)',
  '#5D87C1',
];

const textColorSatellite = [
  'match',
  ['get', 'maki'],
  [
    'beer',
    'restaurant',
    'cafe',
    'restaurant-seafood',
    'teahouse',
    'bbq',
    'bar',
    'restaurant-bbq',
    'restaurant-sushi',
    'restaurant-pizza',
    'restaurant-noodle',
    'ice-cream',
    'fast-food',
    'karaoke',
  ],
  '#fab17b',
  ['lodging'],
  '#dea3f1',
  [
    'shoe',
    'clothing_store',
    'fitness-centre',
    'jewelry-store',
    'shop',
    'bakery',
    'fuel',
    'alcohol-shop',
    'car-rental',
    'florist',
    'garden-centre',
    'watch',
    'furniture',
    'optician',
    'gift',
    'convenience',
    'confectionery',
    'laundry',
    'grocery',
    'commercial',
    'hairdresser',
  ],
  '#60adff',
  [
    'volcano',
    'natural',
    'dog-park',
    'racetrack',
    'cemetery-JP',
    'park',
    'hot-spring',
    'campsite',
    'cemetery',
    'highway-rest-area',
    'park-alt1',
    'playground',
    'mountain',
    'viewpoint',
    'waterfall',
    'wetland',
    'beach',
    'garden',
  ],
  '#98bf65',
  ['dentist', 'doctor', 'hospital', 'veterinary', 'pharmacy', 'hospital-JP'],
  '#ff967e',
  [
    'landmark-JP',
    'stadium',
    'monument-JP',
    'amusement-park',
    'library',
    'castle-JP',
    'cinema',
    'castle',
    'theatre',
    'casino',
    'art-gallery',
    'landmark',
    'bowling-alley',
    'monument',
    'museum',
    'aquarium',
    'zoo',
    'attraction',
  ],
  '#7ebfad',
  'hsl(216, 39%, 74%)',
];

/**
 * POI contain next properties:
 *   - score:reality - probability if POI exists. Possible values are a string from 0 to 1, where 0 - doesn't exist and 1 - exists
 *   - score:closed - probability if POI closes. Possible values are a string from 0 to 1, where 0 - opens and 1 - closes
 *
 * Please keep https://mapbox.atlassian.net/wiki/spaces/RAVETOOL/pages/18841842/Contribute+Tileset+s#Why-I-don%E2%80%99t-see-a-POI%3F
 * doc up to date.
 */
const filterPoiExpression: Expression = [
  'all',
  ['!=', ['get', 'score:closed'], '1'], // do not render POI which is 100% closed
  [
    'step',
    ['zoom'],
    [
      'all',
      ['>=', ['to-number', ['get', 'score:reality']], 0.9],
      ['==', ['get', 'score:closed'], '0'],
    ],
    17, // display POI on zoom < 17 if all conditions above are true
    [
      'all',
      ['>=', ['to-number', ['get', 'score:reality']], 0.5], // display if condition is true and zoom between 17 and 19
      ['<=', ['to-number', ['get', 'score:closed']], 0.6], // display if condition is true and zoom less than 17
    ],
    18, // display POI on zoom >= 17 && zoom < 18 if all conditions above are true
    ['>=', ['to-number', ['get', 'score:reality']], 0.2], // display if condition is true and zoom between 17 and 19
    19, // display POI on zoom >= 18 && zoom < 19 if the condition above is true. Value must be the same as MIN_MAP_POI_SELECTED_ZOOM_LEVEL.
    true, // always display POI if zoom >= 19
  ],
];

const getStreetsBasedDefaultStyle = (params: {
  defaultLayerId: string;
  hoveredLayerId: string;
  selectedLayerId: string;
}) => {
  const { defaultLayerId, hoveredLayerId, selectedLayerId } = params;

  return {
    add: (map) => {
      const style = map.getStyle();

      if (!style.sources[sourceId as string]) {
        map.addSource(sourceId, {
          type: 'vector',
          url: tileset,
        });
      }

      if (!style.layers.some(({ id }) => id === defaultLayerId)) {
        map.addLayer({
          id: defaultLayerId,
          type: 'symbol',
          source: sourceId,
          'source-layer': sourceLayer,
          minzoom: MIN_MAP_POI_ZOOM_LEVEL,
          filter: filterPoiExpression,
          layout: {
            'icon-image': [
              'image',
              ['concat', ['get', 'maki'], '_25px_unselected_vector'],
            ],
            'icon-size': 0.7,
            'text-size': 13,
            'text-field': [
              'case',
              ['in', ';', ['get', 'name']],
              [
                'slice',
                ['to-string', ['get', 'name']],
                0,
                ['index-of', ';', ['get', 'name']],
              ],
              ['to-string', ['get', 'name']],
            ],
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-offset': [0, 0.75],
            'text-anchor': 'top',
          },
          paint: {
            'icon-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              0,
              ['boolean', ['feature-state', 'selected'], false],
              0,
              1,
            ],
            'text-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              0,
              ['boolean', ['feature-state', 'selected'], false],
              0,
              1,
            ],
            'text-color': textColorVector,
            'text-halo-color': '#ffffff',
            'text-halo-width': 1,
          },
        });
      }

      if (!style.layers.some(({ id }) => id === hoveredLayerId)) {
        map.addLayer({
          id: hoveredLayerId,
          type: 'symbol',
          source: sourceId,
          'source-layer': sourceLayer,
          minzoom: MIN_MAP_POI_ZOOM_LEVEL,
          filter: filterPoiExpression,
          layout: {
            'icon-image': [
              'image',
              ['concat', ['get', 'maki'], '_25px_unselected_vector'],
            ],
            'icon-size': 1,
            'icon-offset': [0, 7],
            'icon-anchor': 'bottom',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'text-ignore-placement': true,
            'text-size': 13,
            'text-field': [
              'case',
              ['in', ';', ['get', 'name']],
              [
                'slice',
                ['to-string', ['get', 'name']],
                0,
                ['index-of', ';', ['get', 'name']],
              ],
              ['to-string', ['get', 'name']],
            ],
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-offset': [0, 0.75],
            'text-anchor': 'top',
          },
          paint: {
            'icon-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              1,
              ['boolean', ['feature-state', 'selected'], false],
              0,
              0,
            ],
            'text-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              1,
              ['boolean', ['feature-state', 'selected'], false],
              0,
              0,
            ],
            'text-color': '#ff2e00',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1,
          },
        });
      }

      if (!style.layers.some(({ id }) => id === selectedLayerId)) {
        map.addLayer({
          id: selectedLayerId,
          type: 'symbol',
          source: sourceId,
          'source-layer': sourceLayer,
          minzoom: MIN_MAP_POI_ZOOM_LEVEL,
          filter: filterPoiExpression,
          layout: {
            'text-ignore-placement': true,
            'text-size': 13,
            'text-field': [
              'case',
              ['in', ';', ['get', 'name']],
              [
                'slice',
                ['to-string', ['get', 'name']],
                0,
                ['index-of', ';', ['get', 'name']],
              ],
              ['to-string', ['get', 'name']],
            ],
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-offset': [0, 0.75],
            'text-anchor': 'top',
          },
          paint: {
            'text-opacity': [
              'case',
              ['boolean', ['feature-state', 'hidden'], false],
              0,
              ['boolean', ['feature-state', 'selected'], false],
              1,
              0,
            ],
            'text-color': '#ff2e00',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1,
          },
        });
      }
    },
    remove: (map) =>
      removeLayersWhenMapReady(map, [
        defaultLayerId,
        hoveredLayerId,
        selectedLayerId,
      ]),
  };
};

const getStreetsBasedCircleStyle = (params: {
  circleLayerId: string;
  circleLayerHoveredId: string;
}) => {
  const { circleLayerId, circleLayerHoveredId } = params;

  return {
    add: (map) => {
      const style = map.getStyle();

      if (!style.sources[sourceId as string]) {
        map.addSource(sourceId, {
          type: 'vector',
          url: tileset,
        });
      }

      if (!style.layers.some(({ id }) => id === circleLayerId)) {
        map.addLayer(
          {
            id: circleLayerId,
            type: 'circle',
            source: sourceId,
            'source-layer': sourceLayer,
            minzoom: 17,
            filter: filterPoiExpression,
            layout: {},
            paint: {
              'circle-radius': [
                'interpolate',
                ['exponential', 1.5],
                ['zoom'],
                0,
                2.5,
                19,
                4,
              ],
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 1,
              'circle-color': textColorVector,
              'circle-opacity': [
                'case',
                ['boolean', ['feature-state', 'selected'], false],
                0,
                ['boolean', ['feature-state', 'hover'], false],
                0,
                1,
              ],
              'circle-stroke-opacity': [
                'case',
                ['boolean', ['feature-state', 'selected'], false],
                0,
                ['boolean', ['feature-state', 'hover'], false],
                0,
                1,
              ],
              'circle-translate': [0, -5],
            },
          },
          'road-label',
        );
      }

      if (!style.layers.some(({ id }) => id === circleLayerHoveredId)) {
        map.addLayer(
          {
            id: circleLayerHoveredId,
            type: 'circle',
            source: sourceId,
            'source-layer': sourceLayer,
            minzoom: 17,
            filter: filterPoiExpression,
            layout: {},
            paint: {
              'circle-radius': [
                'interpolate',
                ['exponential', 1.5],
                ['zoom'],
                0,
                3.5,
                19,
                5,
              ],
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 1,
              'circle-color': '#ff2e00',
              'circle-opacity': [
                'case',
                ['boolean', ['feature-state', 'selected'], false],
                0,
                ['boolean', ['feature-state', 'hover'], false],
                1,
                0,
              ],
              'circle-stroke-opacity': [
                'case',
                ['boolean', ['feature-state', 'selected'], false],
                0,
                ['boolean', ['feature-state', 'hover'], false],
                1,
                0,
              ],
              'circle-translate': [0, -5],
            },
          },
          'road-label',
        );
      }
    },
    remove: (map) =>
      removeLayersWhenMapReady(map, [circleLayerId, circleLayerHoveredId]),
  };
};

const getSatelliteBasedDefaultStyle = (layersIdPostfix?: string) => ({
  add: (map) => {
    const style = map.getStyle();

    if (!style.sources[sourceId as string]) {
      map.addSource(sourceId, {
        type: 'vector',
        url: tileset,
      });
    }

    const defaultLayerId = getDefaultLayerId(layersIdPostfix);
    if (!style.layers.some(({ id }) => id === defaultLayerId)) {
      map.addLayer({
        id: defaultLayerId,
        type: 'symbol',
        source: sourceId,
        'source-layer': sourceLayer,
        minzoom: MIN_MAP_POI_ZOOM_LEVEL,
        filter: filterPoiExpression,
        layout: {
          'icon-image': [
            'image',
            ['concat', ['get', 'maki'], '_25px_unselected_vector'],
          ],
          'icon-size': 0.7,
          'text-size': 13,
          'text-field': [
            'case',
            ['in', ';', ['get', 'name']],
            [
              'slice',
              ['to-string', ['get', 'name']],
              0,
              ['index-of', ';', ['get', 'name']],
            ],
            ['to-string', ['get', 'name']],
          ],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-offset': [0, 0.75],
          'text-anchor': 'top',
        },
        paint: {
          'icon-opacity': [
            'case',
            ['boolean', ['feature-state', 'hidden'], false],
            0,
            ['boolean', ['feature-state', 'hover'], false],
            0,
            ['boolean', ['feature-state', 'selected'], false],
            0,
            1,
          ],
          'text-opacity': [
            'case',
            ['boolean', ['feature-state', 'hidden'], false],
            0,
            ['boolean', ['feature-state', 'hover'], false],
            0,
            ['boolean', ['feature-state', 'selected'], false],
            0,
            1,
          ],
          'text-color': textColorSatellite,
          'text-halo-color': '#000000',
          'text-halo-width': 1,
        },
      });
    }

    const hoveredLayerId = getHoveredLayerId(layersIdPostfix);
    if (!style.layers.some(({ id }) => id === hoveredLayerId)) {
      map.addLayer({
        id: hoveredLayerId,
        type: 'symbol',
        source: sourceId,
        'source-layer': sourceLayer,
        minzoom: MIN_MAP_POI_ZOOM_LEVEL,
        filter: filterPoiExpression,
        layout: {
          'icon-image': [
            'image',
            ['concat', ['get', 'maki'], '_25px_unselected_vector'],
          ],
          'icon-size': 1,
          'icon-offset': [0, 7],
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'text-ignore-placement': true,
          'text-size': 13,
          'text-field': [
            'case',
            ['in', ';', ['get', 'name']],
            [
              'slice',
              ['to-string', ['get', 'name']],
              0,
              ['index-of', ';', ['get', 'name']],
            ],
            ['to-string', ['get', 'name']],
          ],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-offset': [0, 0.75],
          'text-anchor': 'top',
        },
        paint: {
          'icon-opacity': [
            'case',
            ['boolean', ['feature-state', 'hidden'], false],
            0,
            ['boolean', ['feature-state', 'hover'], false],
            1,
            ['boolean', ['feature-state', 'selected'], false],
            0,
            0,
          ],
          'text-opacity': [
            'case',
            ['boolean', ['feature-state', 'hidden'], false],
            0,
            ['boolean', ['feature-state', 'hover'], false],
            1,
            ['boolean', ['feature-state', 'selected'], false],
            0,
            0,
          ],
          'text-color': '#ff9780',
          'text-halo-color': '#000000',
          'text-halo-width': 1,
        },
      });
    }

    const selectedLayerId = getSelectedLayerId(layersIdPostfix);
    if (!style.layers.some(({ id }) => id === selectedLayerId)) {
      map.addLayer({
        id: selectedLayerId,
        type: 'symbol',
        source: sourceId,
        'source-layer': sourceLayer,
        minzoom: MIN_MAP_POI_ZOOM_LEVEL,
        filter: filterPoiExpression,
        layout: {
          'text-ignore-placement': true,
          'text-size': 13,
          'text-field': [
            'case',
            ['in', ';', ['get', 'name']],
            [
              'slice',
              ['to-string', ['get', 'name']],
              0,
              ['index-of', ';', ['get', 'name']],
            ],
            ['to-string', ['get', 'name']],
          ],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-offset': [0, 0.75],
          'text-anchor': 'top',
        },
        paint: {
          'text-opacity': [
            'case',
            ['boolean', ['feature-state', 'hidden'], false],
            0,
            ['boolean', ['feature-state', 'selected'], false],
            1,
            0,
          ],
          'text-color': '#ff9780',
          'text-halo-color': '#000000',
          'text-halo-width': 1,
        },
      });
    }
  },
  remove: (map) =>
    removeLayersWhenMapReady(map, [
      getDefaultLayerId(layersIdPostfix),
      getHoveredLayerId(layersIdPostfix),
      getSelectedLayerId(layersIdPostfix),
    ]),
});

const getSatelliteBasedCircleStyle = (layersIdPostfix?: string) => ({
  add: (map) => {
    const style = map.getStyle();

    if (!style.sources[sourceId as string]) {
      map.addSource(sourceId, {
        type: 'vector',
        url: tileset,
      });
    }

    const circleLayerId = getCircleLayerId(layersIdPostfix);
    if (!style.layers.some(({ id }) => id === circleLayerId)) {
      map.addLayer(
        {
          id: circleLayerId,
          type: 'circle',
          source: sourceId,
          'source-layer': sourceLayer,
          minzoom: 17,
          filter: filterPoiExpression,
          layout: {},
          paint: {
            'circle-radius': [
              'interpolate',
              ['exponential', 1.5],
              ['zoom'],
              0,
              2.5,
              19,
              4,
            ],
            'circle-stroke-color': '#2e2e2e',
            'circle-stroke-width': 1,
            'circle-color': textColorSatellite,
            'circle-opacity': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              0,
              1,
            ],
            'circle-stroke-opacity': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              0,
              1,
            ],
            'circle-translate': [0, -5],
          },
        },
        'road-label',
      );
    }

    const circleLayerHoveredId = getCircleLayerHoveredId(layersIdPostfix);
    if (!style.layers.some(({ id }) => id === circleLayerHoveredId)) {
      map.addLayer(
        {
          id: circleLayerHoveredId,
          type: 'circle',
          source: sourceId,
          'source-layer': sourceLayer,
          minzoom: 17,
          filter: filterPoiExpression,
          layout: {},
          paint: {
            'circle-radius': [
              'interpolate',
              ['exponential', 1.5],
              ['zoom'],
              0,
              3.5,
              19,
              5,
            ],
            'circle-stroke-color': '#000000',
            'circle-stroke-width': 1,
            'circle-color': '#ff9780',
            'circle-opacity': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              1,
              0,
            ],
            'circle-stroke-opacity': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              0,
              ['boolean', ['feature-state', 'hover'], false],
              1,
              0,
            ],
            'circle-translate': [0, -5],
          },
        },
        'road-label',
      );
    }
  },
  remove: (map) =>
    removeLayersWhenMapReady(map, [
      getCircleLayerId(layersIdPostfix),
      getCircleLayerHoveredId(layersIdPostfix),
    ]),
});

export const getDefaultLayerStyleData = (mapStyleId?: string) => {
  const layersIdPostfix = getLayersIdUniquePostfix(mapStyleId);

  const defaultLayerId = getDefaultLayerId(layersIdPostfix);
  const hoveredLayerId = getHoveredLayerId(layersIdPostfix);
  const selectedLayerId = getSelectedLayerId(layersIdPostfix);

  let style: unknown = null;
  switch (mapStyleId) {
    case mapStyleTypes.streets:
      style = getStreetsBasedDefaultStyle({
        defaultLayerId,
        hoveredLayerId,
        selectedLayerId,
      });
      break;
    case mapStyleTypes.satellite:
    case mapStyleTypes.hybrid:
      style = getSatelliteBasedDefaultStyle(layersIdPostfix);
      break;
  }

  return {
    style,
    layerId: defaultLayerId,
    hoveredLayerId,
    selectedLayerId,
  };
};

export const getCircleLayerStyleData = (mapStyleId?: string) => {
  const layersIdPostfix = getLayersIdUniquePostfix(mapStyleId);

  const circleLayerId = getCircleLayerId(layersIdPostfix);
  const circleLayerHoveredId = getCircleLayerHoveredId(layersIdPostfix);

  let style: unknown = null;
  switch (mapStyleId) {
    case mapStyleTypes.streets:
      style = getStreetsBasedCircleStyle({
        circleLayerId,
        circleLayerHoveredId,
      });
      break;
    case mapStyleTypes.satellite:
    case mapStyleTypes.hybrid:
      style = getSatelliteBasedCircleStyle(layersIdPostfix);
      break;
  }

  return {
    style,
    layerId: circleLayerId,
    hoveredLayerId: circleLayerHoveredId,
  };
};
